import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

export type VRoute = 'start' | 'result' | GamesRoutes;
export type GamesRoutes =
    'quiz' |
    'foodtest' |
    'chat'
export type FruitType =
    'roll' |
    'cake' |
    'sausage';

export type START_SET_TYPE = {
    router: VRoute;
    fruit: FruitType;
    level: number;
    deskSize: number;
}

const START_SET: START_SET_TYPE = {
    router: 'start',
    fruit: 'roll',
    level: 1,
    deskSize: 1023
}

const getInitialRoute = (): VRoute => {
    const savedCompleted = localStorage.getItem('completed');
    const savedPromo = localStorage.getItem('promo');

    if (!savedCompleted || !savedPromo) {
        return START_SET.router;
    }

    try {
        const parsedCompleted = JSON.parse(savedCompleted);
        if (Array.isArray(parsedCompleted) && parsedCompleted.length >= 3) {
            return 'result';
        }
    } catch {
        return START_SET.router;
    }

    return START_SET.router;
}

const VALID_ROUTES: readonly VRoute[] = ['start', 'quiz', 'foodtest', 'chat', 'result'];
const VALID_FRUITS: readonly FruitType[] = ['roll', 'cake', 'sausage'];

function isVRoute(s: string): s is VRoute {
    return (VALID_ROUTES as readonly string[]).includes(s);
}
function isFruitType(s: string): s is FruitType {
    return (VALID_FRUITS as readonly string[]).includes(s);
}

function applySearchParams(
    params: URLSearchParams,
    setFruitType: (f: FruitType) => void,
    setCurrentRoute: (r: VRoute) => void,
    setQuizStep: (n: number) => void,
    setGameResult: (v: boolean) => void,
) {
    const screen = params.get('screen');
    const char = params.get('char');

    if (char && isFruitType(char)) {
        setFruitType(char);
    }

    if (screen && isVRoute(screen)) {
        setGameResult(false);
        setQuizStep(START_SET.level);
        setCurrentRoute(screen);
    }
}

interface AppContextType {
    currentRoute: VRoute;
    setCurrentRoute: (route: VRoute) => void;
    quizStep: number;
    setQuizStep: (step: number) => void;
    gameResult: boolean;
    switchGameResult: () => void;
    setNextGame: () => void;
    gameStarted: boolean;
    setGameStarted: () => void;
    fruitType: FruitType,
    setFruitType: (fruit: FruitType) => void;
    restart: () => void;
    musicPlay: boolean;
    switchMusicPlay: () => void;
    completed: FruitType[];
    handleCompleted: () => void;
    isMobile: boolean;
    loading: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentRoute, setCurrentRoute] = useState<VRoute>(getInitialRoute);
    const [gameResult, setGameResult] = useState(false);
    const [quizStep, setQuizStep] = useState(START_SET.level);
    const [gameStarted, setGameStartedInner] = useState(false);
    const [fruitType, setFruitType] = useState<FruitType>(START_SET.fruit);
    const [musicPlay, setMusicPlay] = useState(false);
    const [completed, setCompleted] = useState<FruitType[]>(() => {
        const saved = localStorage.getItem('completed');
        return saved ? JSON.parse(saved) : [];
    });
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(true);


    const switchGameResult = () => setGameResult((prevState) => !prevState);
    const switchMusicPlay = () => setMusicPlay((prevState) => !prevState);

    const addCompleted = () => {
        setCompleted(prev => {
            if (prev.includes(fruitType)) return prev;
            if (prev.length >= 3) return prev;
            const newCompleted = [...prev, fruitType];
            localStorage.setItem('completed', JSON.stringify(newCompleted));
            return newCompleted;
        });
    }


    const setNextGame = () => {
        setCurrentRoute((prev) => {
            switch (prev) {
                case 'start':
                    return 'quiz';
                case 'quiz':
                    return 'foodtest';
                case 'foodtest':
                    return 'chat';
                case 'chat':
                    return 'result';
                default:
                    return 'quiz';
            }
        })
        setGameResult(false);
        setGameStartedInner(false);
        setQuizStep(1);
    }

    const restartAndSave = () => {
        setQuizStep(1);
        setGameStartedInner(false);
        setCurrentRoute('start');
        setGameResult(false);
    }

    useEffect(() => {
        setIsMobile(window.innerWidth <= START_SET.deskSize);
        const updateHeight = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
            const isMobile = window.innerWidth <= START_SET.deskSize;
            setIsMobile((prev) => {
                if (prev !== isMobile) {
                    setGameStartedInner(false);
                    setGameResult(false);
                };
                return isMobile;
            });
        };

        updateHeight();
        setLoading(false);
        window.addEventListener('resize', updateHeight);
        window.addEventListener('orientationchange', updateHeight);
        window.addEventListener('touchmove', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);
            window.removeEventListener('orientationchange', updateHeight);
            window.removeEventListener('touchmove', updateHeight);
        };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.get('screen') && !params.get('char')) return;
        applySearchParams(params, setFruitType, setCurrentRoute, setQuizStep, setGameResult);
    }, []);

    return (
        <AppContext.Provider value={{
            currentRoute,
            setCurrentRoute,
            quizStep,
            setQuizStep,
            gameResult,
            switchGameResult,
            setNextGame,
            gameStarted,
            setGameStarted: () => setGameStartedInner(true),
            fruitType,
            setFruitType,
            restart: restartAndSave,
            musicPlay,
            switchMusicPlay,
            completed,
            handleCompleted: addCompleted,
            isMobile,
            loading
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
};